/**
 * Lock policy (ADR-0102 decision 7): statutory text a legal block marks as
 * non-editable and non-removable.
 *
 * A lock is DATA on the compiled node (`locked: true`), stamped by the
 * block's `editPolicy` at compile time, so a persisted revision carries its
 * locks and a replay never needs the block's code to honour them.
 *
 * Semantics, enforced by `applyPatches` / `replayLog` (conflict reason
 * `locked`):
 *
 *  - A locked node and everything inside it cannot be edited (`replaceText`),
 *    removed or moved.
 *  - A node that CONTAINS a locked node cannot be removed or moved (that
 *    would remove / move the statutory text with it); its own title stays
 *    editable.
 *  - Nothing can be inserted INTO a locked node (append, or before / after
 *    one of its children). Inserting before / after a locked node is allowed.
 *  - Signature nodes are always locked — the signing plan depends on them.
 */
import { childLists } from './address.js';
export function summarizeEditPolicy(policy) {
    if (policy == null || policy === 'free')
        return { mode: 'free', lockedSections: [], dynamic: false };
    if (policy === 'locked')
        return { mode: 'locked', lockedSections: [], dynamic: false };
    const lockedSections = [...(policy.lockedSections ?? [])];
    const dynamic = typeof policy.lockNode === 'function';
    return { mode: lockedSections.length > 0 || dynamic ? 'partial' : 'free', lockedSections, dynamic };
}
/**
 * Stamps `locked: true` on the nodes a policy locks — `compileBlock` runs
 * this over every block's output. Returns new node objects for the stamped
 * ones; the input is not mutated. `'free'` returns the input unchanged.
 */
export function applyEditPolicy(nodes, policy) {
    if (policy == null || policy === 'free')
        return nodes;
    if (policy === 'locked')
        return nodes.map((n) => ({ ...n, locked: true }));
    const sections = new Set(policy.lockedSections ?? []);
    const lockNode = policy.lockNode;
    if (sections.size === 0 && !lockNode)
        return nodes;
    const visit = (node, sectionRef, depth) => {
        const ownRef = node.type === 'section' ? node.ref : sectionRef;
        const lock = (node.type === 'section' && sections.has(node.ref)) || (lockNode ? lockNode(node, { ...(ownRef != null ? { sectionRef: ownRef } : {}), depth }) === true : false);
        // A locked node locks its subtree; no need to descend.
        if (lock)
            return { ...node, locked: true };
        const lists = childLists(node);
        if (lists.length === 0)
            return node;
        const copy = structuredClone(node);
        for (const { list } of childLists(copy)) {
            for (let i = 0; i < list.length; i++)
                list[i] = visit(list[i], ownRef, depth + 1);
        }
        return copy;
    };
    return nodes.map((n) => visit(n, undefined, 0));
}
//# sourceMappingURL=lock.js.map